import { defineStore } from 'pinia'
import http from '../services/http'

export type AppRole =
  | 'Admin'
  | 'AreaCoordinator'
  | 'BarangayCoordinator'
  | 'QuickCountEncoder'
  | 'RequestEncoder'
  | 'RequestApprover'

export interface AuthUser {
  id: string
  userName: string
  email: string | null
  role: AppRole
  areaId: string | null
  barangayId: string | null
  pollingPlaceId: string | null
}

const TOKEN_KEY = 'dlm.token'
const USER_KEY = 'dlm.user'
const PERMS_KEY = 'dlm.perms'

function loadUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY)
    return raw ? (JSON.parse(raw) as AuthUser) : null
  } catch {
    return null
  }
}

function loadPerms(): string[] {
  try {
    const raw = localStorage.getItem(PERMS_KEY)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: localStorage.getItem(TOKEN_KEY) as string | null,
    user: loadUser(),
    permissions: loadPerms() as string[],
  }),

  getters: {
    isAuthenticated: (s) => !!s.token,
    role: (s): AppRole | null => s.user?.role ?? null,
    /** True when the signed-in user holds `perm` (Admin implicitly holds all). */
    can:
      (s) =>
      (perm: string): boolean =>
        s.user?.role === 'Admin' || s.permissions.includes(perm),
  },

  actions: {
    async login(usernameOrEmail: string, password: string): Promise<AuthUser> {
      const { data } = await http.post('/auth/login', { usernameOrEmail, password })
      this.setSession(data.accessToken, data.user)
      await this.fetchPermissions()
      return data.user as AuthUser
    },

    setSession(token: string, user: AuthUser) {
      this.token = token
      this.user = user
      localStorage.setItem(TOKEN_KEY, token)
      localStorage.setItem(USER_KEY, JSON.stringify(user))
    },

    async fetchMe(): Promise<AuthUser> {
      const { data } = await http.get('/auth/me')
      this.user = data
      localStorage.setItem(USER_KEY, JSON.stringify(data))
      return data as AuthUser
    },

    /** Load the user's effective permissions (§15 RBAC) — drives the SPA's show/hide. */
    async fetchPermissions(): Promise<string[]> {
      try {
        const { data } = await http.get<string[]>('/me/permissions')
        this.permissions = data
        localStorage.setItem(PERMS_KEY, JSON.stringify(data))
      } catch {
        this.permissions = []
      }
      return this.permissions
    },

    clear() {
      this.token = null
      this.user = null
      this.permissions = []
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(USER_KEY)
      localStorage.removeItem(PERMS_KEY)
    },

    logout() {
      this.clear()
    },
  },
})

/** Landing route for a role after login. */
export function homeRouteForRole(role: AppRole | null): string {
  if (role === 'QuickCountEncoder') return 'quick-count-encoder'
  if (role === 'RequestEncoder' || role === 'RequestApprover') return 'requests'
  return 'dashboard'
}
